import { useCallback, useEffect, useState } from "react";
import Category from "../api/Category";
import Service from "../api/Service";
import {
    Box,
    ButtonBase,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import TextField from "./TextField";
import ToggleButton from "./ToggleButton";
import ServiceView from "./ServiceView";
import Button from "./Button";
import PlusIcon from "../img/Plus_Small.svg";
import DeleteForever from "@mui/icons-material/DeleteForever";
import DragIndicatorRounded from "@mui/icons-material/DragIndicatorRounded";
import useLocalizedDescription from "../hooks/useLocalizedDescription";
import { FONTS } from "../constants/styles";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDroppable } from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";

const SectionView = (props: {
    uuid: string;
    brand_id: string;
    category: Category;
    new_item: boolean;
    onCategoryChange: (category: Category) => void;
    onCategoryDelete: (category: Category) => void;
    editMode: boolean;
    onSelect: (val: Service) => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
    index: number;
    total: number;
    services: Service[];
    onServiceReorder: (val: Service[]) => void;
    onServiceAdd: (categoryKey: string) => void;
    onServiceChange: (val: Service) => void;
}) => {
    const theme = useTheme();
    const {
        category,
        new_item,
        onCategoryChange,
        onCategoryDelete,
        editMode,
        onSelect,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onMoveUp,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onMoveDown,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        index,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        total,
        services,
        onServiceReorder,
        onServiceAdd,
        onServiceChange,
    } = props;

    const categoryName = useLocalizedDescription(
        category.name,
        category.nameOriginal
    );

    const [show, setShow] = useState(category.enabled);
    const [name, setName] = useState(category.name);
    const [nameEdit, setNameEdit] = useState(new_item);

    useEffect(() => {
        setName(category.name);
        setShow(category.enabled);
    }, [category.name, category.enabled]);

    const toggleShow = useCallback(
        (val: boolean) => {
            setShow(val);
            category.enabled = val;
            onCategoryChange(category);
        },
        [category, onCategoryChange]
    );

    const onNameChange = useCallback(() => {
        setNameEdit(false);
        if (name.trim().length === 0) {
            setName(category.name);
            return;
        }
        category.name = name;
        onCategoryChange(category);
    }, [name, category, onCategoryChange]);

    const moveService = (index: number, direction: "up" | "down") => {
        if (index < 0 || index >= services.length) return;
        const newServices = [...services];

        const targetIndex = direction === "up" ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= services.length) return;

        [newServices[index], newServices[targetIndex]] = [
            newServices[targetIndex],
            newServices[index],
        ];

        newServices.forEach((service, i) => (service.position = i));
        onServiceReorder(newServices);
    };

    // Category-level sortable (prefixed ID)
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: "cat:" + category.key, disabled: !editMode });

    const sortableStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    // Droppable zone for receiving services from other categories
    const { setNodeRef: setDroppableRef } = useDroppable({
        id: "drop:" + category.key,
    });

    if (editMode)
        return (
            <Stack
                ref={setNodeRef}
                style={sortableStyle}
                {...attributes}
                key={category.name}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    py={1.5}
                    px={2}
                    bgcolor={alpha(theme.palette.primary.main, 0.06)}
                >
                    {/* Drag handle */}
                    <Box
                        {...listeners}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "grab",
                            touchAction: "none",
                            color: "text.secondary",
                            flexShrink: 0,
                        }}
                    >
                        <DragIndicatorRounded sx={{ fontSize: 20 }} />
                    </Box>

                    {/* Delete button */}
                    <ButtonBase
                        onClick={() => onCategoryDelete(category)}
                        sx={{ flexShrink: 0 }}
                    >
                        <DeleteForever
                            color="primary"
                            sx={{ fontSize: 22 }}
                        />
                    </ButtonBase>

                    {/* Category name (editable) */}
                    {nameEdit ? (
                        <TextField
                            autoFocus
                            variant="outlined"
                            value={name}
                            onChange={(e) => setName(e.currentTarget.value)}
                            onBlur={onNameChange}
                            sx={{
                                flex: 1,
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                            }}
                        />
                    ) : (
                        <ButtonBase
                            sx={{
                                flex: 1,
                                justifyContent: "flex-start",
                                borderRadius: "12px",
                                minHeight: 42,
                            }}
                            onClick={() => setNameEdit(true)}
                        >
                            <Typography
                                fontWeight={600}
                                fontSize="16px"
                                fontFamily={FONTS.display}
                                textAlign="start"
                                px={1}
                            >
                                {name}
                            </Typography>
                        </ButtonBase>
                    )}

                    {/* Toggle */}
                    <ToggleButton value={show} onClick={toggleShow} />
                </Stack>

                {/* Services — sortable within parent DndContext, droppable for cross-category */}
                <div ref={setDroppableRef}>
                    <SortableContext
                        items={services.map((s) => "svc:" + s.key)}
                        strategy={verticalListSortingStrategy}
                    >
                        {services.map((x, idx) => (
                            <ServiceView
                                key={x.key}
                                service={x}
                                editMode={editMode}
                                onChange={onServiceChange}
                                onSelect={onSelect}
                                onMoveUp={() => moveService(idx, "up")}
                                onMoveDown={() => moveService(idx, "down")}
                                index={idx}
                                total={services.length}
                            />
                        ))}
                    </SortableContext>
                    {services.length === 0 && (
                        <Stack
                            alignItems="center"
                            py={2}
                            sx={{ opacity: 0.4 }}
                        >
                            <Typography fontSize="13px" color="text.secondary">
                                Drop services here
                            </Typography>
                        </Stack>
                    )}
                </div>

                <Stack width="100%" alignItems="center" marginY={2}>
                    <Button
                        variant="medium"
                        color="primary"
                        icon={PlusIcon}
                        onClick={() => onServiceAdd(category.key)}
                    >
                        Add service
                    </Button>
                </Stack>
            </Stack>
        );

    return (
        <Stack key={category.name}>
            <Typography
                padding={2}
                fontWeight={600}
                fontSize="18px"
                lineHeight="23.4px"
                fontFamily={FONTS.display}
            >
                {categoryName}
            </Typography>
            {services
                .filter((x) => x.enabled || editMode)
                .map((x, idx) => (
                    <Stack key={x.key} mb={0.5} px={2}>
                        <ServiceView
                            service={x}
                            editMode={editMode}
                            onChange={onServiceChange}
                            onSelect={onSelect}
                            onMoveUp={() => moveService(idx, "up")}
                            onMoveDown={() => moveService(idx, "down")}
                            index={idx}
                            total={services.length}
                        />
                    </Stack>
                ))}
        </Stack>
    );
};

export default SectionView;
